// ── SENZA RETE ─────────────────────────────────────────────────────
// A bordo campo il segnale va e viene. Quasi tutto quello che si fa in
// campo — presenze, sostituti, una lezione — Firestore lo scrive sul
// telefono e lo manda quando la rete torna. Alcune cose invece la rete la
// vogliono adesso: le Cloud Functions, Google Calendar, e le operazioni che
// devono trovare documenti che sul telefono potrebbero non esserci (tutte
// le lezioni di una serie, tutti i pagamenti di una lezione). Quelle,
// senza rete, devono dirlo subito invece di fallire in silenzio o, peggio,
// fare metà del lavoro con quello che il telefono ha in memoria.

// `navigator.onLine` dice di sicuro solo il no: "online" vuol dire che c'è
// una rete, non che arrivi a internet. Il caso del wifi senza internet lo
// copre l'attesa delle scritture, che non si conclude finché il server non
// risponde.
export const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false

export const NEEDS_NETWORK = "needs-network"

export const NEEDS_NETWORK_MESSAGE = "Senza rete non si può fare: riprova quando torna la connessione"

export class NetworkRequiredError extends Error {
	constructor(message = NEEDS_NETWORK_MESSAGE) {
		super(message)
		this.name = "NetworkRequiredError"
		this.code = NEEDS_NETWORK
	}
}

// Ferma un'operazione che senza rete non può andare a buon fine, prima
// che cominci: meglio un "serve la rete" adesso che un errore generico
// tra settanta secondi, o un lavoro fatto a metà.
export const requireNetwork = message => {
	if (isOffline()) throw new NetworkRequiredError(message)
}

// Gli errori che vogliono dire "non c'era rete", da qualunque parte
// arrivino. Una callable senza rete risponde `internal` — lo stesso codice
// di un errore vero del server — quindi quello conta solo se il telefono
// dice di essere offline.
const NETWORK_ERROR_CODES = [
	NEEDS_NETWORK,
	"auth/network-request-failed",
	"functions/unavailable",
	"functions/deadline-exceeded",
	"unavailable",
]

export const isNetworkError = err =>
	NETWORK_ERROR_CODES.includes(err?.code) ||
	(err?.code === "functions/internal" && isOffline()) ||
	(err instanceof TypeError && isOffline())

// Il messaggio da far vedere: quello di chi ha fermato l'operazione per la
// rete, oppure quello che il chiamante avrebbe detto comunque.
export const networkAwareMessage = (err, fallback) => {
	if (err?.code === NEEDS_NETWORK) return err.message
	if (isNetworkError(err)) return NEEDS_NETWORK_MESSAGE
	return fallback
}

// ── MODIFICHE NON ANCORA INVIATE ───────────────────────────────────
// Firestore non dice quante scritture ha in coda, ma sa dire quando la
// coda di adesso si è svuotata (`waitForPendingWrites`). Se la risposta
// tarda, vuol dire che c'è qualcosa che aspetta il server.
//
// Oltre alla coda di Firestore ci sono le operazioni ancora in corso
// nell'app: una lezione modificata scrive allievi e pagamenti solo dopo che
// il server ha confermato la lezione, e finché non l'ha fatto quelle
// scritture non sono neanche in coda. Chi le avvia le fa contare con
// `track`.

// Con la rete buona una scrittura si conferma in qualche decimo di
// secondo: una pastiglia che lampeggia a ogni salvataggio sarebbe rumore.
// Si dice solo un'attesa che dura.
export const PENDING_NOTICE_DELAY = 2000

// La coda si guarda anche senza che nessuno lo chieda: una scrittura
// partita da un punto qualsiasi dell'app la si vede lo stesso.
export const PENDING_CHECK_INTERVAL = 5000

export const createSyncTracker = ({ waitForQueue, isOnline = () => !isOffline() }) => {
	let state = { online: isOnline(), pending: false, syncedAt: null }
	const listeners = new Set()
	const idleWaiters = new Set()

	let operations = 0
	let checkingQueue = false
	let noticeTimer = null
	let pollTimer = null
	// qualcosa è andato storto nell'attesa: alla fine non si dice "inviato"
	let failedWhilePending = false

	const setState = patch => {
		const next = { ...state, ...patch }
		if (
			next.online === state.online &&
			next.pending === state.pending &&
			next.syncedAt === state.syncedAt
		)
			return
		state = next
		listeners.forEach(listener => listener(state))
	}

	const isBusy = () => operations > 0 || checkingQueue

	const refresh = () => {
		if (isBusy()) {
			if (state.pending || noticeTimer) return
			noticeTimer = setTimeout(() => {
				noticeTimer = null
				if (isBusy()) setState({ pending: true })
			}, PENDING_NOTICE_DELAY)
			return
		}

		clearTimeout(noticeTimer)
		noticeTimer = null
		idleWaiters.forEach(resolve => resolve())
		idleWaiters.clear()
		if (!state.pending) {
			failedWhilePending = false
			return
		}

		// l'attesa che si vedeva è finita: se è finita bene, lo si dice
		setState({
			pending: false,
			syncedAt: failedWhilePending ? state.syncedAt : Date.now(),
		})
		failedWhilePending = false
	}

	const checkQueue = () => {
		if (checkingQueue) return
		checkingQueue = true
		refresh()

		Promise.resolve()
			.then(waitForQueue)
			.catch(err => {
				// cambio di utente o Firestore chiuso: quelle scritture non
				// sono più affar nostro, ma non sono nemmeno "inviate"
				failedWhilePending = true
				console.info("Attesa delle scritture interrotta:", err?.code ?? err)
			})
			.then(() => {
				checkingQueue = false
				refresh()
			})
	}

	const track = promise => {
		operations += 1
		refresh()

		Promise.resolve(promise)
			.catch(() => {
				failedWhilePending = true
			})
			.then(() => {
				operations -= 1
				// prima la coda, poi il conto: le scritture che l'operazione
				// ha lasciato in coda tengono accesa l'attesa senza buchi
				checkQueue()
				refresh()
			})

		return promise
	}

	const onConnectivityChange = () => {
		setState({ online: isOnline() })
		checkQueue()
	}

	const onVisibilityChange = () => {
		if (document.visibilityState === "visible") onConnectivityChange()
	}

	const start = () => {
		if (pollTimer) return
		window.addEventListener("online", onConnectivityChange)
		window.addEventListener("offline", onConnectivityChange)
		document.addEventListener("visibilitychange", onVisibilityChange)
		pollTimer = setInterval(() => {
			if (document.visibilityState !== "hidden") checkQueue()
		}, PENDING_CHECK_INTERVAL)
		// le scritture rimaste in coda dall'ultima volta che l'app era aperta
		onConnectivityChange()
	}

	const stop = () => {
		window.removeEventListener("online", onConnectivityChange)
		window.removeEventListener("offline", onConnectivityChange)
		document.removeEventListener("visibilitychange", onVisibilityChange)
		clearInterval(pollTimer)
		clearTimeout(noticeTimer)
		pollTimer = null
		noticeTimer = null
	}

	// Si risolve quando non c'è più niente da mandare: né in coda, né in
	// corso nell'app.
	const whenIdle = () =>
		new Promise(resolve => {
			idleWaiters.add(resolve)
			checkQueue()
		})

	const subscribe = listener => {
		listeners.add(listener)
		return () => listeners.delete(listener)
	}

	return { start, stop, track, checkQueue, whenIdle, subscribe, getState: () => state }
}
